<template>
  <el-form
      ref="addressForm"
      :model="request_address"
      :rules="addressRules">
    <div class="grid gap_m gap-h_xs el-form-grid">
      <el-form-item
          class="span-12"
          prop="customer_address_1"
          label="Address">
        <small
            v-if="googlePlacesOn"
            :style="{ 'color': googlePlaceSelected ? '#67C23A' : '#F56C6C' }">
          <span v-if="googlePlaceSelected">
            <i class="el-icon-check"></i>
            Google place selected
          </span>
          <span v-else>
            Google place required
          </span>
        </small>
        <el-input
            v-model="request_address.customer_address_1"
            v-google-places="autocomplete"
            :placeholder="address1Placeholder"
            name="address1"
            @selected="placeSelected" />
      </el-form-item>

      <el-form-item
          class="span-12"
          prop="customer_address_2"
          label="Address 2">
        <el-input
            v-model="request_address.customer_address_2"
            placeholder="Address line 2"
            name="address2" />
      </el-form-item>

      <el-form-item
          class="span-6"
          prop="customer_country"
          label="Country">
        <CountryInput
            v-model="request_address.customer_country"
            :disabled="googlePlacesOn" />
      </el-form-item>
      <el-form-item
          class="span-6"
          prop="customer_state"
          label="State">
        <CountryStateInput
            v-model="request_address.customer_state"
            :disabled="disableStateInput"
            :input-type="'select'"
            :country="request_address.customer_country" />
      </el-form-item>
      <el-form-item
          class="span-6"
          prop="customer_city"
          label="City">
        <el-input
            v-model="request_address.customer_city"
            placeholder="City"
            :disabled="googlePlacesOn"
            name="city" />
      </el-form-item>

      <el-form-item
          class="span-6"
          prop="customer_zip"
          label="Zip code">
        <el-input
            v-model="request_address.customer_zip"
            placeholder="Zip code"
            :disabled="googlePlacesOn"
            name="zip_code" />
      </el-form-item>
    </div>
  </el-form>
</template>

<script>
import { cloneDeep, get, find, isEmpty } from "lodash";
import CountryInput from "@/components/Shared/AddressForm/CountryInput";
import CountryStateInput from "@/components/Shared/AddressForm/CountryStateInput";
import { createNamespacedHelpers as h } from "vuex";
const { mapGetters: sharedGetters } = h("shared");
const { mapGetters: requestGetters } = h("db/requests");

let normalizeIncomingAddress = (address) => {
  let addr = cloneDeep(address);
  if (Array.isArray(addr.customer_address_1)) {
    addr.customer_address_1 = String(addr.customer_address_1); // Some addresses are normalized by fedex as array. e.g [ "CALLE 18", "# 85C" ]
  }
  return addr;
};

export default {
  name: "GooglePlacesAddressForm",
  components: {
    CountryInput,
    CountryStateInput,
  },
  props: {
    autocomplete: {
      type: Boolean,
      required: true,
    },
    currentAddress: {
      type: Object,
      required: true,
    },
  },
  data() {
    var validateSelectedCountry = (rule, value, callback) => {
      if (value === "") {
        callback(new Error("Country can't be blank"));
      } else if (!this.isAvailableMarket(value)) {
        callback(
          new Error(
            "The selected country is not available for shipping\. Please choose another customer address\."
          )
        );
      } else {
        callback();
      }
    };
    return {
      googlePlaceSelected: false,
      request_address: {
        customer_address_1: null,
        customer_address_2: null,
        customer_city: null,
        customer_state: null,
        customer_country: null,
        customer_zip: null,
      },
      addressRules: {
        customer_address_1: [
          {
            required: true,
            message: "Address can't be blank",
            trigger: "change",
          },
        ],
        customer_country: [
          { validator: validateSelectedCountry, trigger: "blur" },
        ],
        customer_state: [
          {
            required: true,
            message: "State can't be blank",
            trigger: "change",
          },
        ],
        customer_city: [
          { required: true, message: "City can't be blank", trigger: "change" },
        ],
        customer_zip: [
          {
            required: true,
            message: "Zip code can't be blank",
            trigger: "change",
          },
        ],
      },
      isValidSelectedMarket: true,
      plc_action: "",
    };
  },
  computed: {
    ...sharedGetters(["markets"]),
    ...requestGetters(["requestInProgress"]),
    address1Placeholder() {
      return this.autocomplete ? "Start typing address" : "Address line 1";
    },
    googlePlacesOn() {
      return this.autocomplete;
    },
    googlePlacesOff() {
      return !this.autocomplete;
    },
    isDomestic() {
      return ["US", "CA"].includes(this.request_address.customer_country);
    },
    isJapan() {
      return ["JP"].includes(this.request_address.customer_country);
    },
    disableStateInput() {
      return (
        isEmpty(this.request_address.customer_country) ||
        this.googlePlacesOn ||
        !this.isValidSelectedMarket
      );
    },
  },
  watch: {
    autocomplete(on) {
      if (!on) {
        this.googlePlaceSelected = false;
        this.validateForm();
      }
    },
  },
  watch: {
    currentAddress() {
      this.normalize();
    },
  },
  created() {},
  mounted() {
    this.normalize();
  },
  updated() {
    if (this.googlePlacesOff) this.validateForm();
  },
  methods: {
    normalize() {
      this.request_address = normalizeIncomingAddress(this.currentAddress);
      this.$refs.addressForm.clearValidate();
      this.$nextTick(() => {
        this.validateForm();
      });
    },
    placeSelected(gPlace) {
      this.request_address.customer_address_1 = gPlace.address1;
      if (gPlace.address2)
        this.request_address.customer_address_2 = gPlace.address2;
      this.request_address.customer_city = gPlace.city;
      this.request_address.customer_zip = gPlace.zip;
      this.request_address.customer_country = gPlace.countryCode;
      this.request_address.customer_state = this.getStateSelector(gPlace);
      this.googlePlaceSelected = true;

      this.validateForm();
    },
    getStateSelector(gPlace) {
      let selector = "";
      if (this.isJapan) {
        selector = gPlace.stateLabel;
      } else if (this.isDomestic) {
        selector = gPlace.stateCode;
      } else {
        selector = gPlace.state;
      }
      return selector;
    },
    validateForm: function () {
      let formValidated = false;
      if (this.$refs.addressForm) {
        this.$refs.addressForm.validate((valid) => {
          formValidated = valid;
          if (!valid) {
            // ignore
          }
        });
      }
      this.isValidSelectedMarket = this.isAvailableMarket(
        this.request_address.customer_country
      );
      let isValid =
        this.googlePlacesOn && this.isValidSelectedMarket
          ? this.googlePlaceSelected && this.selectedGooglePlaceIsComplete()
          : formValidated;

      this.$emit("update", {
        address: this.request_address,
        isValid,
      });
    },
    resetFields() {
      this.$refs.addressForm.resetFields();
    },
    getMarketByCountry(country) {
      return find(this.markets, (m) => m.members.includes(country));
    },
    isAvailableMarket(country) {
      let available = false;
      let plc_action = this.requestInProgress.general.type;
      if (this.requestInProgress.general.type == "one_off") {
        plc_action = "oneoff";
      }
      let market = this.getMarketByCountry(country);
      if (market) {
        available = get(
          this.markets,
          `${market.code}.options.plc.permits.${plc_action}`,
          false
        );
      }
      return available;
    },
    selectedGooglePlaceIsComplete() {
      return (
        !!this.request_address.customer_city &&
        !!this.request_address.customer_zip &&
        !!this.request_address.customer_country &&
        !!this.request_address.customer_state
      );
    },
  },
};
</script>

<style scoped></style>
